/* eslint-disable react-hooks/exhaustive-deps */
import {Box, Grid} from '@amzn/awsui-components-react'
import * as queryString from 'query-string'
import React, {useEffect, useState} from 'react'
import {RouteComponentProps} from 'react-router-dom'
import {
    fetchAcDetailsByIapAndFnsku,
    fetchIapDetails,
    fetchIapRelationshipsWithNamesByIaps,
    fetchIoglsByIap,
    fetchIogsByIap,
    queryIapsByMetadata,
    fetchProgramById,
} from '../../backend/backend'
import {getIapPageUrl, getMetadataSearchPageUrl} from '../../backend/internalUrls'
import {
    AvailabilityConsumer,
    AvailabilityConsumerDetails,
    Iap,
    IapMetadataResult,
    IapRelationshipDetails,
    IapRelationshipWithNames,
    Iog,
    Iogl,
    MetadataQuery,
} from '../../backend/model'
import {parseErrorMessage} from '../../utils/errors'
import {isNotEmptyQuery, isValidFnsku, isValidNumericId} from '../../validation/InputValidator'
import AjaxStatusIndicator from '../basic/AjaxStatusIndicator'
import IoglTable from '../Iog/tables/IoglTable'
import IapSearchPanel from './IapSearchPanel'
import IapSummaryPanel from './IapSummaryPanel'
import AcTable from './tables/AcTable'
import IapRelationshipTable from './tables/IapRelationshipTable'
import IogTable from './tables/IogTable'
import {constructAdvancedSearchQuery, toAdvancedQueryString} from '../search/SearchUtils'
import {MetaDataTable} from '../metadata/MetadataTable'
import {AxiosError} from 'axios'
import {ADVANCED_SEARCH_TAB_ID, SIMPLE_SEARCH_TAB_ID} from '../metadata/Constants'

type IapPageProps = RouteComponentProps<{ iapId: string; fnsku: string }>
const BASE_PATH = '/iaps'
const MAX_RESULTS = '50'

const IapPage: React.FC<IapPageProps> = (props) => {
    const [iap, setIap] = useState<Iap>()
    const [iapRelationshipsWithNames, setIapRelationshipsWithNames] = useState<IapRelationshipWithNames[]>([])
    const [iogs, setIogs] = useState<Iog[]>([])
    const [iogls, setIogls] = useState<Iogl[]>([])
    const [availabilityConsumers, setAvailabilityConsumers] = useState<AvailabilityConsumer[]>([])
    const [programDescriptions, setProgramDescriptions] = useState<{[key: string]: string}>({})
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessages, setErrorMessages] = useState<string[]>([])
    const [advancedSearchResult, setAdvancedSearchResult] = useState<IapMetadataResult[]>([])
    const [metadataNextToken, setMetadataNextToken] = useState<string | null>(null)
    const queries = queryString.parse(props.location.search) || ''
    const searchViewFromUrl = (queries.view as string)?.trim() || SIMPLE_SEARCH_TAB_ID

    const advancedSearchQuery = constructAdvancedSearchQuery(queries)
    const advancedSearchQueryString = toAdvancedQueryString(advancedSearchQuery)

    const resetState = (): void => {
        setIap(undefined)
        setIapRelationshipsWithNames([])
        setIogs([])
        setIogls([])
        setAvailabilityConsumers([])
        setProgramDescriptions({})
        setErrorMessages([])
        setAdvancedSearchResult([])
        setMetadataNextToken(null)
    }

    const iapIdFromUrl = props.match.params.iapId?.trim()
    const fnskuFromUrl = queryString.parse(props.location.search).fnsku as string

    useEffect(() => {
        searchInfoByIapId(iapIdFromUrl, fnskuFromUrl)
    }, [iapIdFromUrl, fnskuFromUrl, searchViewFromUrl])

    useEffect(() => {
        if (searchViewFromUrl == ADVANCED_SEARCH_TAB_ID) {
            searchByIapMetadata(advancedSearchQuery)
        }
    }, [searchViewFromUrl, advancedSearchQueryString])

    const searchInfoByIapId = async (iapId: string, fnsku?: string) => {
        resetState()

        if (isValidNumericId(iapId) && (!fnsku || isValidFnsku(fnsku))) {
            setIsLoading(true)

            try {
                const iap = (await fetchIapDetails(iapId)).data
                setIap(iap)

                const [iogls, iogs, availabilityConsumers, iapRelationships] = await Promise.all([
                    fetchIoglsByIap(iap.iapId),
                    fetchIogsByIap(iap.iapId),
                    fetchAcDetailsByIapAndFnsku(iap.iapId, fnsku),
                    fetchIapRelationshipsWithNamesByIaps(iap.iapId),
                ])

                setIogls(iogls.data)
                setIogs(iogs.data)
                setAvailabilityConsumers(availabilityConsumers.data)
                setIapRelationshipsWithNames(iapRelationships.data)

                // Fetch program descriptions for unique program IDs
                const uniqueProgramIds = [...new Set(availabilityConsumers.data.map(ac => ac.key.fulfillmentProgramId).filter(Boolean))];
                const programDescs: {[key: string]: string} = {};
                
                await Promise.allSettled(uniqueProgramIds.map(async (programId) => {
                    try {
                        const programResp = await fetchProgramById(programId);
                        programDescs[programId] = programResp.data.description;
                    } catch (e: any) {
                        if (e.response?.status === 404) {
                            programDescs[programId] = 'Program does not exist in PJS.';
                        } else {
                            programDescs[programId] = 'Failed to fetch program description.';
                        }
                    }
                }));
                
                setProgramDescriptions(programDescs);
            } catch (error: unknown) {
                console.error(error)
                if (error instanceof Error) {
                    setErrorMessages([parseErrorMessage(error as AxiosError)])
                }
                setIap(undefined)
            } finally {
                setIsLoading(false)
            }
        }
    }

    const getAdvancedSearchPayload = (query: MetadataQuery, initialLoad: boolean): MetadataQuery => ({
        ...query,
        ...(!initialLoad && {nextToken: metadataNextToken}),
        maxResults: MAX_RESULTS,
    })

    const handleMetadataQuery = async (query: MetadataQuery, initialLoad: boolean) => {
        if (!isNotEmptyQuery(query)) return
        setIsLoading(true)

        try {
            const requestPayload = getAdvancedSearchPayload(query, initialLoad)
            const iapMetadataResults = (await queryIapsByMetadata(requestPayload)).data

            const displayResults: IapMetadataResult[] = iapMetadataResults.iapMetadata.map(({iapId, matchedMetadata}) =>
                matchedMetadata.reduce((result: IapMetadataResult, {key, value}) => {
                    result[key] = value
                    return result
                }, {iapId}),
            )

            if (initialLoad) {
                setAdvancedSearchResult(displayResults)
            } else {
                setAdvancedSearchResult(prevResults => [...prevResults, ...displayResults])
            }

            setMetadataNextToken(iapMetadataResults.nextToken)
        } catch (error: any) {
            console.error(error)
            setErrorMessages([parseErrorMessage(error)])
            setAdvancedSearchResult([])
        } finally {
            setIsLoading(false)
        }
    }

    const searchByIapMetadata = async (query: MetadataQuery) => {
        resetState()
        handleMetadataQuery(query, true)
    }

    const fetchIapMetadata = async (query: MetadataQuery) => {
        handleMetadataQuery(query, false)
    }

    function getFileName(tableName: string): string {
        const fileNamePrefix = `IAP ${iapIdFromUrl}` + (fnskuFromUrl ? ` FNSKU ${fnskuFromUrl}` : '')
        return `${fileNamePrefix} ${tableName}`
    }

    function getMetaDataFileName(tableName: string): string {
        const fileNamePrefix = 'IAP' + advancedSearchQueryString

        return `${fileNamePrefix} ${tableName}`
    }

    return (
        <div>
            <Box variant="h1">Inventory Availability Pool (IAP)</Box>
            <IapSearchPanel iapId={iapIdFromUrl}
                            fnsku={fnskuFromUrl}
                            advancedSearchQuery={advancedSearchQuery}
                            simpleSearchHandler={(iapId?: string, fnsku?: string) => props.history.push(getIapPageUrl(iapId, fnsku))}
                            advancedSearchHandler={(query: MetadataQuery) => {
                                props.history.push(getMetadataSearchPageUrl(BASE_PATH, query))
                            }}
                            initialTab={searchViewFromUrl}
            />
            <br/>
            <AjaxStatusIndicator errorMessage={errorMessages.join(' ')} loading={isLoading}/>
            <br/>
            {
                iap &&
                <IapSummaryPanel iap={iap}/>
            }
            <br/>
            {
                iap &&
                <Grid gridDefinition={[
                    {colspan: {default: 12, xxs: 8}},
                    {colspan: {default: 12, xxs: 4}},
                    {colspan: {default: 12, xxs: 8}},
                    {colspan: {default: 12, xxs: 4}},
                ]}>
                    <IoglTable tableItems={iogls} fileName={getFileName('IOGL Table')}/>
                    <IogTable tableItems={iogs} fileName={getFileName('IOG Table')}/>
                    <AcTable
                        tableItems={flattenAcs(availabilityConsumers)}
                        additionalValue={{fnsku: fnskuFromUrl, programDescriptions}}
                        fileName={getFileName('AC Table')}/>
                    <IapRelationshipTable
                        tableItems={flattenIapRelationships(iapRelationshipsWithNames)}
                        additionalValue={{iapId: iapIdFromUrl, fnsku: fnskuFromUrl}}
                        fileName={getFileName('IAP Relationship Table')}/>
                </Grid>
            }
            {
                advancedSearchResult.length > 0 &&
                <Grid gridDefinition={[{colspan: {default: 12, xxs: 12}}]}>
                    <MetaDataTable
                        metadataType={'IAP'}
                        tableItems={advancedSearchResult}
                        fileName={getMetaDataFileName('IAP Metadata table')}
                        fetchDataHandler={fetchIapMetadata}
                        advancedSearchPayload={getAdvancedSearchPayload(advancedSearchQuery, false)}
                    />
                </Grid>
            }
        </div>
    )
}

const flattenAcs = (items: AvailabilityConsumer[]): AvailabilityConsumerDetails[] => items.map(ac => (
    {
        id: ac.id,
        marketplaceId: ac.key.marketplaceId,
        merchantCustomerId: ac.key.merchantCustomerId,
        fulfillmentProgramId: ac.key.fulfillmentProgramId,
        fulfillmentRegionId: ac.key.fulfillmentRegionId,
    }
))

const flattenIapRelationships = (items: IapRelationshipWithNames[]): IapRelationshipDetails[] => items.map(r => (
    {
        firstIapId: r.iapRelationship.firstIapId,
        firstIapName: r.firstIapName,
        secondIapId: r.iapRelationship.secondIapId,
        secondIapName: r.secondIapName,
        directionality: r.iapRelationship.directionality,
    }
))

export default IapPage
